import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { EMPLOYEE_NAV } from "./_nav";
import { api, errFrom, absUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Camera, Upload, Building2, Check, X, ImagePlus } from "lucide-react";

function tryGeolocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  });
}

export default function EmployeeDealerships() {
  const [dealerships, setDealerships] = useState([]);
  const [selected, setSelected] = useState("");
  const [files, setFiles] = useState([]); // {file, preview}
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  async function loadAll() {
    try {
      const [dl, mine] = await Promise.all([
        api.get("/dealerships"),
        api.get("/employee/my-uploads"),
      ]);
      setDealerships(dl.data);
      setRecent(mine.data.dealership_tags.slice(0, 6));
    } catch (e) { toast.error(errFrom(e)); }
  }
  useEffect(() => { loadAll(); }, []);

  function addFiles(list) {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    const max = 10;
    const combined = [...files, ...arr.map((f) => ({ file: f, preview: URL.createObjectURL(f) }))].slice(0, max);
    if ([...files, ...arr].length > max) toast.message(`Max ${max} photos per submission — extra ignored.`);
    setFiles(combined);
  }
  function removeFile(idx) { setFiles(files.filter((_, i) => i !== idx)); }

  async function submit() {
    if (!selected) return toast.error("Select a dealership");
    if (!files.length) return toast.error("Select or capture at least one image");
    setLoading(true);
    try {
      const geo = await tryGeolocation();
      const fd = new FormData();
      fd.append("dealership_id", selected);
      if (geo) {
        fd.append("latitude", String(geo.latitude));
        fd.append("longitude", String(geo.longitude));
      }
      files.forEach((f) => fd.append("files", f.file));
      await api.post("/employee/dealership-tag", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} posted`);
      setFiles([]); setSelected("");
      loadAll();
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  return (
    <AppShell nav={EMPLOYEE_NAV} title="Dealership Tags" subtitle="Employee">
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 brand-surface rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-lg bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center"><Building2 size={20} /></div>
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Post key tags</h2>
              <p className="text-zinc-500 text-sm">Add one or many photos. Date, time, GPS and your name are embedded on every image.</p>
            </div>
          </div>

          <label className="text-overline">Dealership</label>
          <div className="mt-2">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="h-12 bg-[#0A0A0A] border-white/10" data-testid="dealership-select-trigger">
                <SelectValue placeholder="Select a dealership" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-white">
                {dealerships.map((d) => (<SelectItem key={d.id} value={d.id} data-testid={`dealership-option-${d.id}`}>{d.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <label className="text-overline">Photos · {files.length}/10</label>
              {files.length > 0 && <button onClick={() => setFiles([])} className="text-xs text-zinc-500 hover:text-white" data-testid="clear-photos-button">Clear all</button>}
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {files.map((f, idx) => (
                <div key={idx} className="relative rounded-lg overflow-hidden border border-white/10 group" data-testid={`photo-thumb-${idx}`}>
                  <img src={f.preview} alt="" className="w-full h-32 object-cover" />
                  <button onClick={() => removeFile(idx)} className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full p-1 opacity-90 group-hover:opacity-100" aria-label="Remove" data-testid={`remove-photo-${idx}`}>
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ))}
              {files.length < 10 && (
                <div className="grid grid-cols-1 gap-2">
                  <label className="brand-surface-2 rounded-lg p-3 cursor-pointer hover-lift flex flex-col items-center justify-center text-center min-h-[64px]" data-testid="camera-capture-button">
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    <Camera className="text-[#FF3B30]" size={18} />
                    <div className="text-[11px] mt-1 text-zinc-400">Capture</div>
                  </label>
                  <label className="brand-surface-2 rounded-lg p-3 cursor-pointer hover-lift flex flex-col items-center justify-center text-center min-h-[64px]" data-testid="upload-image-button">
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    <ImagePlus className="text-[#FF3B30]" size={18} />
                    <div className="text-[11px] mt-1 text-zinc-400">Add</div>
                  </label>
                </div>
              )}
            </div>
          </div>

          <Button onClick={submit} disabled={loading} className="mt-6 bg-[#FF3B30] hover:bg-[#E02920] h-12 px-8 w-full sm:w-auto" data-testid="submit-dealership-tag-button">
            {loading ? "Uploading…" : (<><Check size={18} className="mr-2" />Post {files.length > 0 ? `${files.length} photo${files.length > 1 ? "s" : ""}` : ""}</>)}
          </Button>
          <p className="text-xs text-zinc-500 mt-3">Each photo is timestamped, geo-tagged and watermarked with your name for audit &amp; legal traceability.</p>
        </div>

        <div className="lg:col-span-2">
          <div className="brand-surface rounded-2xl p-6" data-testid="recent-tags">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Your recent tags</h3>
              <span className="text-overline">{recent.length}</span>
            </div>
            {recent.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">Nothing yet.</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {recent.map((t) => (
                  <div key={t.id} className="rounded-lg overflow-hidden border border-white/10 bg-[#0A0A0A]" data-testid={`recent-tag-${t.id}`}>
                    <div className="relative">
                      <img src={absUrl(t.image_url || t.images?.[0]?.url)} alt="" className="w-full h-32 object-cover" />
                      {(t.images?.length || 0) > 1 && (
                        <span className="absolute top-1.5 right-1.5 bg-black/75 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-white/10 text-white">+{t.images.length - 1}</span>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-semibold truncate">{t.dealership_name}</div>
                      <div className="text-[10px] text-zinc-500">{new Date(t.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
